import {
  AccountSettingsPage,
  AiSettingsPage,
  AlarmManagementPage,
  AlarmNotificationsPage,
  ApiConnectionsPage,
  AssistantHistoryPage,
  TeamMemoryPage,
  ApiCredentialsPage,
  CustomCollectorsPage,
  OAuthAppsPage,
  MqttBrokersPage,
  ModbusDevicesPage,
  EnipDevicesPage,
  Dnp3DevicesPage,
  OpcConnectionsPage,
  PiConnectionsPage,
  FileSourcesPage,
  IgnitionGatewaysPage,
  VTScadaConnectionsPage,
  DatabaseConnectionsPage,
  HistorianProvidersPage,
  PlatformLicensePage,
  PlatformOverview,
  PlatformTLSPage,
  PlatformUpdatesPage,
  PlcCapabilitiesPage,
  PlcFleetRolloutsPage,
  PlcOverviewPage,
  PlcProtocolServersPage,
  PlcRuntimeFleetPage,
  ProjectsPage,
  SystemSettings,
  UserManagementPage,
} from "./sections";

const NAV_GROUPS = [
  {
    title: "Platform",
    items: [
      { id: "platform-overview", label: "Overview", component: PlatformOverview },
      {
        id: "gateway-settings",
        label: "Gateway Settings",
        component: SystemSettings,
      },
      { id: "projects-list", label: "Projects", component: ProjectsPage },
    ],
  },
  {
    title: "Licensing",
    items: [
      {
        id: "platform-license",
        label: "License",
        component: PlatformLicensePage,
        requireLicensing: true,
      },
      {
        id: "platform-updates",
        label: "Updates",
        component: PlatformUpdatesPage,
        requireLicensing: true,
      },
      {
        id: "platform-tls",
        label: "Web Server / TLS",
        component: PlatformTLSPage,
        adminOnly: true,
        requireLicensing: true,
      },
    ],
  },
  {
    title: "Connectors",
    items: [
      {
        id: "database-connections",
        label: "Database",
        component: DatabaseConnectionsPage,
      },
      {
        id: "historian-providers",
        label: "Historians",
        component: HistorianProvidersPage,
      },
      {
        id: "api-connections",
        label: "HTTP",
        component: ApiConnectionsPage,
      },
      {
        id: "custom-collectors",
        label: "Custom Connectors",
        component: CustomCollectorsPage,
      },
      {
        id: "ignition-gateways",
        label: "Ignition",
        component: IgnitionGatewaysPage,
        product: "intelligence",
      },
      {
        id: "vtscada-connections",
        label: "VTScada",
        component: VTScadaConnectionsPage,
        product: "intelligence",
      },
      {
        id: "file-sources",
        label: "Documents",
        component: FileSourcesPage,
        product: "intelligence",
      },
      {
        id: "api-credentials",
        label: "Credentials",
        component: ApiCredentialsPage,
      },
      {
        id: "oauth-apps",
        label: "OAuth Apps",
        component: OAuthAppsPage,
        adminOnly: true,
      },
    ],
  },
  {
    title: "Devices",
    items: [
      {
        id: "modbus-devices",
        label: "Modbus",
        component: ModbusDevicesPage,
        product: "scada",
      },
      {
        id: "enip-devices",
        label: "EtherNet/IP",
        component: EnipDevicesPage,
        product: "scada",
      },
      {
        id: "dnp3-devices",
        label: "DNP3",
        component: Dnp3DevicesPage,
        product: "scada",
      },
      {
        id: "mqtt-brokers",
        label: "MQTT",
        component: MqttBrokersPage,
        product: "scada",
      },
      {
        id: "opc-connections",
        label: "OPC UA",
        component: OpcConnectionsPage,
        // Dual-use: a SCADA device driver and a first-class intelligence asset
        // source, so it stays visible under both SKUs (unlike its neighbors).
      },
      {
        id: "pi-connections",
        label: "PI System",
        component: PiConnectionsPage,
        product: "intelligence",
      },
    ],
  },
  {
    title: "Servers",
    items: [
      {
        id: "plc-protocol-services",
        label: "Modbus TCP",
        component: PlcProtocolServersPage,
        props: { defaultService: "modbus" },
        product: "scada",
      },
      {
        id: "opcua-server",
        label: "OPC UA",
        component: PlcProtocolServersPage,
        props: { defaultService: "opcua" },
        product: "scada",
      },
      {
        id: "s7comm-server",
        label: "S7Comm",
        component: PlcProtocolServersPage,
        props: { defaultService: "s7comm" },
        product: "scada",
      },
    ],
  },
  {
    title: "PLC",
    items: [
      {
        id: "plc-overview",
        label: "Overview",
        component: PlcOverviewPage,
        product: "scada",
      },
      {
        id: "plc-runtime-fleet",
        label: "Runtimes",
        component: PlcRuntimeFleetPage,
        product: "scada",
      },
      {
        id: "plc-deployments",
        label: "Applications",
        component: PlcFleetRolloutsPage,
        product: "scada",
      },
    ],
  },
  {
    title: "Accounts",
    items: [
      { id: "accounts-settings", label: "Account Settings", component: AccountSettingsPage },
      { id: "user-management", label: "User Management", component: UserManagementPage, adminOnly: true },
    ],
  },
  {
    title: "API",
    items: [
      {
        id: "api-ai-settings",
        label: "AI Settings",
        component: AiSettingsPage,
      },
      {
        id: "assistant-history",
        label: "Assistant History",
        component: AssistantHistoryPage,
      },
      {
        id: "team-memory",
        label: "Team Memory",
        component: TeamMemoryPage,
      },
    ],
  },
  {
    title: "Alarms",
    items: [
      {
        id: "alarms-management",
        label: "Management",
        component: AlarmManagementPage,
        product: "scada",
      },
      {
        id: "alarms-notifications",
        label: "Notifications",
        component: AlarmNotificationsPage,
        adminOnly: true,
        product: "scada",
      },
    ],
  },
];

// Support-only PLC pages keep stable deep links (linked from Overview and
// support workflows) without occupying the sidebar.
const ADVANCED_PLC_ROUTES = Object.freeze({
  "plc-compatibility": { id: "plc-compatibility", label: "Compatibility", component: PlcCapabilitiesPage, product: "scada" },
});

const ROUTES = NAV_GROUPS.reduce((acc, group) => {
  group.items.forEach((item) => {
    acc[item.id] = item;
  });
  return acc;
}, { ...ADVANCED_PLC_ROUTES });

// Old PLC URLs remain valid entry points, but they immediately resolve to the
// job-oriented hierarchy. The runtime-host/controller distinction is carried
// in a query parameter so both objects stay separate and only one inventory
// page mounts (and polls) at a time.
const LEGACY_PLC_ROUTES = Object.freeze({
  "plc-controllers": { id: "plc-runtime-fleet", search: "?view=controllers" },
  "plc-edge-runtimes": { id: "plc-runtime-fleet", search: "?view=hosts" },
  "plc-fleet-rollouts": { id: "plc-deployments", search: "" },
  "plc-protocol-servers": { id: "plc-protocol-services", search: "" },
  "plc-capabilities": { id: "plc-compatibility", search: "" },
});

const mergeLegacySearch = (search, requiredSearch) => {
  const params = new URLSearchParams(search || "");
  const required = new URLSearchParams(requiredSearch || "");
  required.forEach((value, key) => {
    params.set(key, value);
  });
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
};

// A nav item with no `product` field is visible under every SKU. `products`
// is the caller's entitled set (e.g. ["scada"]); pass it as null/undefined
// to skip gating entirely — used by call sites that don't have an
// entitlement to check against.
export function hasProductAccess(item, products) {
  if (!item?.product) return true;
  if (!products) return true;
  return products.includes(item.product);
}

// "Could this user reach this page at all" — admin, licensing and SKU gating.
// Deliberately excludes the operator-configurable sidebar visibility (see
// nav-visibility.js): an unavailable item isn't worth offering a toggle for,
// a merely hidden one is.
export function isNavItemAvailable(item, { isAdmin, licensingEnabled, products } = {}) {
  if (item?.adminOnly && !isAdmin) return false;
  if (item?.requireLicensing && !licensingEnabled) return false;
  return hasProductAccess(item, products);
}

export function resolveGatewaySection(sectionId, search = "", products = null) {
  const direct = ROUTES[sectionId];
  if (direct) {
    if (!hasProductAccess(direct, products)) return null;
    return { id: sectionId, search, redirected: false };
  }
  const legacy = LEGACY_PLC_ROUTES[sectionId];
  if (!legacy) return null;
  if (!hasProductAccess(ROUTES[legacy.id], products)) return null;
  return {
    id: legacy.id,
    search: mergeLegacySearch(search, legacy.search),
    redirected: true,
  };
}

export { ADVANCED_PLC_ROUTES, LEGACY_PLC_ROUTES, NAV_GROUPS, ROUTES };
